import { useMutation } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { DownloadBox } from "@/atoms";
import { constantsData, identifiersData, namesData, statusTypesData } from "@/data";
import { exportAllTracksMutation } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayErrorHandler, getUniqueRandomId } from "@/utils";

const useExportAllTracks = () => {
    const [exporting, setExporting] = useState(false);

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [exportAllTracksAction, { loading }] = useMutation(exportAllTracksMutation);

    const {
        exportAllTracksPending: exportAllTracksPendingToastName,
        exportAllTracksSuccess: exportAllTracksSuccessToastName,
    } = namesData.toasts;

    const { toast: toastId } = identifiersData.ids;

    const exportAllTracks = async () => {
        setExporting(true);

        try {
            await exportAllTracksAction({
                onCompleted: (data) => {
                    setExporting(false);

                    const randomId = getUniqueRandomId();

                    toast.success(
                        () => (
                            <DownloadBox
                                id={`${exportAllTracksSuccessToastName}_${toastId}_${randomId}`}
                                link={data.exportAllTracks}
                                message={t("messages.downloadMsgs.download", { entity: t("tracks.name") })}
                            />
                        ),
                        {
                            closeButton: true,
                            toastId: `${exportAllTracksSuccessToastName}_${toastId}_${randomId}`,
                        },
                    );

                    dispatch(
                        openAlert(
                            t("messages.exportMsgs.success", { entity: t("tracks.name") }),
                            statusTypesData.success,
                        ),
                    );
                },
                variables: { toCsv: true },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        } finally {
            toast.dismiss(`${exportAllTracksPendingToastName}_${toastId}`);
        }
    };

    useEffect(
        () => {
            if (loading && exporting) {
                toast.info(() => t("messages.exportMsgs.pending", { entity: t("tracks.name") }), {
                    toastId: `${exportAllTracksPendingToastName}_${toastId}`,
                });
            }
        },
        [loading, exporting], //eslint-disable-line
    );

    return { exportAllTracks };
};

export default useExportAllTracks;
